import getDevicePropsArr from '@/constants/device-props';
import { FilterType, FilterOperator } from '@/types/analytics/analytics.types';
import { objectMetadataRowPath, PageParameterId } from '@pendo/middleware';
import { eventPropNameMapper } from '@/data-explorer/helpers/common.util';
import { v4 as uuid } from 'uuid';
import get from 'lodash/get';
import union from 'lodash/union';
import isObject from 'lodash/isObject';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';
import toLower from 'lodash/toLower';
import isNumber from 'lodash/isNumber';
import { i18n_t as $t } from '@/utils/i18n';
import { formatObjectPropertyLabel } from '@/object-analytics/object-property-display.helper';

const INTEGER = 'integer';
const FLOAT = 'float';
const BOOLEAN = 'boolean';
const STRING = 'string';
const DATE = 'date';
const TIME = 'time';
const LIST = 'list';

const getOperatorMap = () => {
    return {
        [FilterOperator.Equal]: { label: '=', operator: '==', name: $t('eventPropertyFilter.is equal to') },
        [FilterOperator.NotEqual]: { label: '!=', operator: '!=', name: $t("eventPropertyFilter.isn't equal to") },
        [FilterOperator.Contains]: { label: '~', operator: 'contains', name: $t('eventPropertyFilter.contains') },
        [FilterOperator.NotContains]: {
            label: '!~',
            operator: '!contains',
            name: $t("eventPropertyFilter.doesn't contain"),
        },
        [FilterOperator.GreaterOrEqual]: {
            label: '>=',
            operator: '>=',
            name: $t('eventPropertyFilter.is greater than or equal to'),
        },
        [FilterOperator.LessOrEqual]: {
            label: '<=',
            operator: '<=',
            name: $t('eventPropertyFilter.is less than or equal to'),
        },
        [FilterOperator.Empty]: { label: '()', operator: 'empty', name: $t('eventPropertyFilter.is empty') },
        [FilterOperator.NotEmpty]: { label: '(x)', operator: '!empty', name: $t("eventPropertyFilter.isn't empty") },
        [FilterOperator.True]: {
            label: 'is true',
            operator: '==',
            value: true,
            name: $t('eventPropertyFilter.is true'),
        },
        [FilterOperator.False]: {
            label: 'is false',
            operator: '==',
            value: false,
            name: $t('eventPropertyFilter.is false'),
        },
    };
};

export function convertFilterOperatorToOperator (filterOperator) {
    const operatorMap = getOperatorMap();
    const operatorSettings = operatorMap[filterOperator];
    if (!operatorSettings) return undefined;

    if (filterOperator === FilterOperator.True || filterOperator === FilterOperator.False) {
        return {
            operator: operatorSettings.operator,
            value: operatorSettings.value,
        };
    }

    return { operator: operatorSettings.operator };
}

export function convertOperatorToFilterOperator (operator, schema, value) {
    const operatorMap = getOperatorMap();
    const filterOperators = Object.keys(operatorMap);
    for (const key of filterOperators) {
        if (operatorMap[key].operator === operator) {
            if (schema === BOOLEAN && value === true) {
                return FilterOperator.True;
            }
            if (schema === BOOLEAN && value === false) {
                return FilterOperator.False;
            }

            return key;
        }
    }

    return undefined;
}

export function getOperatorsOptions (schema) {
    const operatorMap = getOperatorMap();
    switch (schema) {
        case BOOLEAN:
            return [operatorMap[FilterOperator.True], operatorMap[FilterOperator.False]];
        case STRING: {
            return [
                operatorMap[FilterOperator.Equal],
                operatorMap[FilterOperator.NotEqual],
                operatorMap[FilterOperator.Contains],
                operatorMap[FilterOperator.NotContains],
                operatorMap[FilterOperator.Empty],
                operatorMap[FilterOperator.NotEmpty],
            ];
        }
        case INTEGER:
        case FLOAT:
        case 'number':
            return [
                operatorMap[FilterOperator.Equal],
                operatorMap[FilterOperator.NotEqual],
                operatorMap[FilterOperator.GreaterOrEqual],
                operatorMap[FilterOperator.LessOrEqual],
                operatorMap[FilterOperator.Empty],
                operatorMap[FilterOperator.NotEmpty],
            ];
        case LIST:
            return [
                operatorMap[FilterOperator.Contains],
                operatorMap[FilterOperator.NotContains],
                operatorMap[FilterOperator.Empty],
                operatorMap[FilterOperator.NotEmpty],
            ];
        default: {
            return [];
        }
    }
}

export function getOperatorNameByLabel (label) {
    const operatorMap = getOperatorMap();

    return get(operatorMap[label], 'name', '');
}

/**
 * Operators that form a logical pair for metadata comparison (boolean / empty rules).
 * Returns [selected label, opposite label] using translated display names, or null.
 */
export function getVsComparisonPillLabels (operatorLabel) {
    if (!operatorLabel) return null;
    const map = getOperatorMap();
    const pairs = [
        [FilterOperator.True, FilterOperator.False],
        [FilterOperator.False, FilterOperator.True],
        [FilterOperator.Empty, FilterOperator.NotEmpty],
        [FilterOperator.NotEmpty, FilterOperator.Empty],
    ];
    for (const [selected, opposite] of pairs) {
        if (operatorLabel === selected) {
            return [map[selected].name, map[opposite].name];
        }
    }

    return null;
}

export const METADATA_COMPARISON_MAX_VALUES = 4;

export const COMPARISON_EXCLUDED_OPERATORS = [
    '!=',
    'contains',
    '!contains',
    'empty',
    '!empty',
    '>=',
    '<=',
];

/** Coerce metadata compare values to strings for groupBy pinning; the middleware narrowing
 * clause turns numeric strings back into numeric literals. */
export function normalizeComparisonValuesForGroupBy (comparisonValues) {
    if (!Array.isArray(comparisonValues)) return [];

    return comparisonValues
        .filter((value) => value !== undefined && value !== '')
        .map((value) => (value === null ? 'null' : String(value)));
}

export const DEFAULT_VALUE_JOIN_DELIMITER = ', ';

export const APP_HEALTH_METADATA_VALUE_JOIN_DELIMITER = 'vs';

export const COMPARE_BY_OPERATOR = 'compareBy';

export function operatorNeedsValue (operator) {
    const labels = [
        FilterOperator.Equal,
        FilterOperator.NotEqual,
        FilterOperator.Contains,
        FilterOperator.NotContains,
        FilterOperator.GreaterOrEqual,
        FilterOperator.LessOrEqual,
    ];

    return labels.includes(operator);
}

export function operatorSupportsMultipleValues (operator) {
    const labels = [FilterOperator.GreaterOrEqual, FilterOperator.LessOrEqual];

    return !labels.includes(operator);
}

export function isFilterValid ({ field, operator, comparisonValues }) {
    return !!(field && operator && (comparisonValues?.length || !operatorNeedsValue(operator)));
}

export function stripFilterForPersist (filter) {
    const persisted = {
        id: filter.id,
        field: filter.field,
        kind: filter.kind,
        filterType: filter.filterType,
        operator: filter.operator,
        comparisonValues: filter.comparisonValues,
    };
    if (filter.group !== undefined) persisted.group = filter.group;
    if (filter.valueSchema !== undefined) persisted.valueSchema = filter.valueSchema;

    return persisted;
}

function filterKey (filter) {
    return [
        filter?.id,
        filter?.field,
        filter?.kind,
        filter?.filterType,
        filter?.group,
        filter?.operator,
        JSON.stringify(filter?.comparisonValues ?? filter?.value ?? null),
    ].join('|');
}

export function filtersMatch (left, right) {
    if (left.length !== right.length) return false;
    const rightKeys = new Set(right.map(filterKey));

    return left.every((filter) => rightKeys.has(filterKey(filter)));
}

/**
 * Gets the union event property names of all given targets
 *
 * @param targets array of full target objects with the following partial structure
 * {
 *  kind: String,
 *  appId?: Number
 *  platform? String
 *  eventPropertyNameList? Array<String>
 *  eventPropertyConfigurations? Array<Object>
 * }
 */
export function eventPropNames (targets) {
    return union(...targets.map(eventPropNameMapper));
}

/**
 * @param {FnProps} params Config object forwarded to `getDevicePropsArr`
 * @returns Array of device props with standardized key names
 */
export function getDeviceProps (params) {
    return getDevicePropsArr(params).map((deviceProp) => {
        return {
            name: deviceProp.DisplayName,
            description: deviceProp.description,
            filterType: FilterType.Device,
            eventPropertyName: deviceProp.DisplayName,
            eventPropertyField: deviceProp.field,
            eventPropertyKind: deviceProp.kind,
            eventPropertySchema: deviceProp.Type,
            eventPropertyElementType: deviceProp.ElementType,
            automaticEventProperty: deviceProp.isAutomatic,
            eventPropertyFieldKey: `__pendo__device_${deviceProp.field}`,
        };
    });
}

export function getCustomEventProperties (eventProperties = []) {
    return eventProperties
        .map((ep) => {
            return {
                name: ep.DisplayName,
                description: ep.description,
                filterType: FilterType.Custom,
                eventPropertyName: ep.DisplayName,
                eventPropertySchema: ep.Type,
                eventPropertyElementType: ep.ElementType,
                eventPropertyFieldKey: ep.field,
                eventPropertyField: ep.field,
                eventPropertyKind: ep.kind,
                uniqueValues: ep.uniqueValues,
            };
        })
        .filter((ep) => ep && get(ep, 'eventPropertySchema') !== 'time');
}

export function getHistoricalMetadata (historical = []) {
    return historical.map((metadata) => {
        return {
            name: metadata.DisplayName,
            description: metadata.description,
            filterType: FilterType.HistoricalMetadata,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType,
            eventPropertyFieldKey: metadata.field,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getAccountMetadata (account = []) {
    return account.map((metadata) => {
        return {
            name: metadata.DisplayName,
            description: metadata.description,
            filterType: FilterType.Account,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getVisitorMetadata (visitor = []) {
    return visitor.map((metadata) => {
        return {
            name: metadata.DisplayName,
            description: metadata.description,
            filterType: FilterType.Visitor,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getParentAccountMetadata (parentAccount = []) {
    return parentAccount.map((metadata) => {
        return {
            name: metadata.DisplayName,
            description: metadata.description,
            filterType: FilterType.ParentAccount,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getObjectMetadata (objectMetadata = []) {
    return objectMetadata.map((metadata) => {
        return {
            name: metadata.DisplayName,
            description: metadata.description,
            filterType: FilterType.ObjectMetadata,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: objectMetadataRowPath(metadata.objectProperty, metadata.field),
            eventPropertyKind: 'objectMetadata',
            eventPropertyGroup: metadata.objectProperty,
            objectProperty: metadata.objectProperty,
            objectPropertyDisplayName: metadata.groupDisplayName,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getSessionProperties (sessionProperties = []) {
    return sessionProperties.map((metadata) => {
        return {
            name: metadata.DisplayName,
            description: metadata.description,
            filterType: FilterType.Utm,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType,
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getLocationProps () {
    return [
        {
            name: $t('eventPropertyFilter.Country'),
            filterType: FilterType.Location,
            eventPropertyName: 'Country',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: 'country',
            eventPropertyField: 'country',
            eventPropertyKind: '',
            uniqueValues: [],
        },
        {
            name: $t('eventPropertyFilter.State/Region'),
            filterType: FilterType.Location,
            eventPropertyName: 'State/Region',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: 'country_region',
            eventPropertyField: 'region',
            eventPropertyKind: '',
            uniqueValues: [],
        },
    ];
}

export function getAutomaticMetadataFieldDescriptions (fieldName) {
    switch(fieldName) {
        case '__pendo__device_name':
            return $t('eventPropertyFilterMultiselect.The web browser, such as Chrome or Safari, or the mobile device model where the event happened.');
        case '__pendo__device_deviceType':
            return $t('eventPropertyFilterMultiselect.The kind of device where the event happened, such as desktop, tablet, or phone.');
        case '__pendo__device_version':
            return $t('eventPropertyFilterMultiselect.The version of the web browser or mobile operating system where the event happened.');
        case '__pendo__device_os':
            return $t('eventPropertyFilterMultiselect.The operating system on the device where the event happened, such as Windows, macOS, iOS, or Android.');
        case '__pendo__device_appVersion':
            return $t('eventPropertyFilterMultiselect.For mobile apps - The version of your app that the visitor was using when the event happened.');
        case '__pendo__device_sdkVersion':
            return $t('eventPropertyFilterMultiselect.The version of the Pendo SDK installed in your app when the event happened. Use it to troubleshoot data collection issues.');
        case 'server':
            return $t('eventPropertyFilterMultiselect.serverDescription');
        case 'country':
            return $t('eventPropertyFilterMultiselect.The country where the event happened, based on the visitor\'s IP address.');
        case 'country_region':
            return $t('eventPropertyFilterMultiselect.The state, province, or region where the event happened, based on the visitor\'s IP address.');
        default:
            return $t('eventPropertyFilterMultiselect.Automatically collected by Pendo');
    }
}

export const AGENTIC_DATA_GROUP = 'Agentic Data';

// disabledTooltip keeps the group visible but unselectable, so users can see why it can't be picked.
export function getAgenticDataProps ({ disabledTooltip = null } = {}) {
    // Each field is the tracked item type the backend expects for a funnel step or path starting point.
    return [
        { name: $t('eventPropertyFilter.Tracked use case'), eventPropertyName: 'Tracked use case', field: 'trackedusecase' },
        { name: $t('eventPropertyFilter.Tracked issue'), eventPropertyName: 'Tracked issue', field: 'trackedissue' },
        { name: $t('eventPropertyFilter.Tracked intent metric'), eventPropertyName: 'Tracked intent metric', field: 'trackedintentmetric' },
    ].map(({ name, eventPropertyName, field }) => ({
        name,
        filterType: FilterType.AgenticData,
        eventPropertyName,
        eventPropertySchema: STRING,
        eventPropertyElementType: STRING,
        eventPropertyFieldKey: field,
        eventPropertyField: field,
        eventPropertyKind: '',
        uniqueValues: [],
        ...(disabledTooltip && { disabled: true, disabledTooltip }),
    }));
}

// A tracked item belongs to one agent. When a step's agent changes, its tracked item filter keeps its type but needs a
// new item; when the step is no longer an agent, the filter no longer applies.
export function resetAgenticFiltersForAgent (filters, agentId) {
    if (!agentId) return filters.filter((filter) => filter.filterType !== FilterType.AgenticData);

    return filters.map((filter) => (filter.filterType === FilterType.AgenticData ? { ...filter, comparisonValues: [] } : filter));
}

export function getTopLevelProps () {
    return [
        {
            name: $t('eventPropertyFilter.Server name'),
            filterType: FilterType.EventTopLevel,
            eventPropertyName: 'Server name',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: 'server',
            eventPropertyField: 'server',
            eventPropertyKind: '',
            uniqueValues: [],
        },
    ];
}

export function getObjectProperties (objectProperties = []) {
    return objectProperties.map((metadata) => {
        const baseLabel = metadata.DisplayName || metadata.field;
        const label = formatObjectPropertyLabel({
            DisplayName: baseLabel,
            field: metadata.field,
        });

        return {
            name: label,
            description: metadata.description,
            filterType: FilterType.ObjectProperty,
            eventPropertyName: label,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getPageParameterProps () {
    return [
        {
            name: $t('eventPropertyFilter.Page parameter'),
            filterType: FilterType.PageParameter,
            eventPropertyName: 'Page parameter',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: PageParameterId,
            eventPropertyField: PageParameterId,
            eventPropertyKind: '',
            uniqueValues: [],
        },
    ];
}

/**
 * Create event property options (for the EP filter multiselect) based on the
 * given targets and known metadata.
 *
 * @param targets array of full target objects with the following partial structure
 * {
 *  kind: String,
 *  appId?: Number
 *  platform? String
 *  eventPropertyNameList? Array<String>
 *  eventPropertyConfigurations? Array<Object>
 * }
 */

export function setupEventProperties ({
    targets,
    eventProperties,
    historicalMetadata,
    accountMetadata,
    visitorMetadata,
    parentAccountMetadata,
    sessionProperties,
    objectProperties = [],
    objectMetadata = [],
    setupForWebAnalytics = false,
    pagesWithPageParams,
    includeLocation,
    includeServer,
    showDeviceProperties = true,
    includeAgenticData = false,
    agenticDataDisabledTooltip = null,
}) {
    const deviceProps = getDeviceProps({ targets });
    const custom = getCustomEventProperties(eventProperties);
    const historical = getHistoricalMetadata(historicalMetadata);
    const account = getAccountMetadata(accountMetadata);
    const visitor = getVisitorMetadata(visitorMetadata);
    const parentAccount = getParentAccountMetadata(parentAccountMetadata);
    const sessionProps = getSessionProperties(sessionProperties);
    const objectProps = getObjectProperties(objectProperties);
    const objectMetadataProps = getObjectMetadata(objectMetadata);
    const locationProps = includeLocation ? getLocationProps() : [];
    const topLevelProps = includeServer ? getTopLevelProps() : [];

    return mapEventProperties({
        automaticEventProperties: deviceProps,
        customEventProperties: custom,
        historicalMetadata: historical,
        sessionProperties: sessionProps,
        objectProperties: objectProps,
        objectMetadata: objectMetadataProps,
        grouped: true,
        showDeviceProperties,
        accountMetadata: account,
        visitorMetadata: visitor,
        parentAccountMetadata: parentAccount,
        setupForWebAnalytics,
        pagesWithPageParams,
        locationEventProperties: locationProps,
        topLevelEventProperties: topLevelProps,
        agenticDataProperties: includeAgenticData ? getAgenticDataProps({ disabledTooltip: agenticDataDisabledTooltip }) : [],
    });
}

export function mapEventProperties ({
    automaticEventProperties = [],
    locationEventProperties = [],
    topLevelEventProperties = [],
    customEventProperties = [],
    historicalMetadata = [],
    sessionProperties = [],
    accountMetadata = [],
    visitorMetadata = [],
    parentAccountMetadata = [],
    objectProperties = [],
    objectMetadata = [],
    grouped = false,
    setupForWebAnalytics = false,
    pagesWithPageParams = [],
    showDeviceProperties = true,
    agenticDataProperties = [],
}) {
    const hasSessionProperties = sessionProperties.length > 0;
    const pageParam = pagesWithPageParams.length > 0 ? getPageParameterProps() : [];

    if (grouped) {
        const res = [];

        if (agenticDataProperties.length) {
            res.push({
                eventType: AGENTIC_DATA_GROUP,
                eventPropertyName: uuid(),
                eventProps: agenticDataProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Agentic data'),
            });
        }

        if (showDeviceProperties) {
            res.push({
                eventType: 'Device Data',
                eventPropertyName: uuid(),
                eventProps: automaticEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Device data'),
            });
        }

        if (pageParam.length) {
            res.push({
                eventType: 'Page Data',
                eventPropertyName: uuid(),
                eventProps: pageParam,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Page data'),
            });
        }

        if (topLevelEventProperties.length) {
            res.push({
                eventType: 'Event Data',
                eventPropertyName: uuid(),
                eventProps: topLevelEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Event data'),
            });
        }

        if (locationEventProperties.length) {
            res.push({
                eventType: 'Location Data',
                eventPropertyName: uuid(),
                eventProps: locationEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Location data'),
            });
        }

        if (hasSessionProperties) {
            const sessionProps = {
                eventType: 'Session Properties',
                eventPropertyName: uuid(),
                eventProps: sessionProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Session properties'),
            };

            if (setupForWebAnalytics) {
                res.unshift(sessionProps);
            } else {
                res.push(sessionProps);
            }
        }

        if (customEventProperties.length) {
            res.push({
                eventType: 'Custom',
                eventPropertyName: uuid(),
                eventProps: customEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Custom event properties'),
            });
        }

        if (accountMetadata.length) {
            res.push({
                eventType: 'Account Metadata',
                eventPropertyName: uuid(),
                eventProps: accountMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Account metadata'),
            });
        }

        if (visitorMetadata.length) {
            res.push({
                eventType: 'Visitor Metadata',
                eventPropertyName: uuid(),
                eventProps: visitorMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Visitor metadata'),
            });
        }

        if (parentAccountMetadata.length) {
            res.push({
                eventType: 'Parent Account Metadata',
                eventPropertyName: uuid(),
                eventProps: parentAccountMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Parent Account metadata'),
            });
        }

        if (historicalMetadata.length) {
            res.push({
                eventType: 'Historical Metadata',
                eventPropertyName: uuid(),
                eventProps: historicalMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Historical metadata'),
            });
        }

        if (objectProperties.length) {
            const objectPropsGroup = {
                eventType: 'Object Properties',
                eventPropertyName: uuid(),
                eventProps: objectProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('common.Objects'),
            };
            const sessionAtTop = setupForWebAnalytics && hasSessionProperties;
            res.splice(sessionAtTop ? 1 : 0, 0, objectPropsGroup);
        }

        if (objectMetadata.length) {
            const byObjectProperty = new Map();
            objectMetadata.forEach((option) => {
                const key = option.objectProperty;
                if (!byObjectProperty.has(key)) byObjectProperty.set(key, []);
                byObjectProperty.get(key).push(option);
            });
            byObjectProperty.forEach((options, objectProperty) => {
                res.push({
                    eventType: `Object Metadata: ${objectProperty}`,
                    eventPropertyName: uuid(),
                    eventProps: options,
                    eventPropertyFieldKey: uuid(),
                    groupLabel: $t('eventPropertyFilter.Object metadata: {objectProperty}', {
                        objectProperty,
                    }),
                });
            });
        }

        return res;
    }

    const eventProps = [
        ...customEventProperties,
        ...automaticEventProperties,
        ...historicalMetadata,
        ...locationEventProperties,
        ...topLevelEventProperties,
        ...pageParam,
    ];

    if (hasSessionProperties) {
        if (setupForWebAnalytics) {
            eventProps.unshift(...sessionProperties);
        } else {
            eventProps.push(...sessionProperties);
        }
    }

    if (objectProperties.length) {
        if (setupForWebAnalytics && hasSessionProperties) {
            eventProps.splice(sessionProperties.length, 0, ...objectProperties);
        } else {
            eventProps.unshift(...objectProperties);
        }
    }

    if (objectMetadata.length) {
        eventProps.push(...objectMetadata);
    }

    return eventProps;
}

export function coerceEventPropertyType (option, schema) {
    const rawOption = option;
    try {
        // BE sends us stringified uniqueValues and FE needs to parse to remove escape characters.
        option = JSON.parse(option);
    } catch {
        // do nothing
    }
    const stringifiedOption = isObject(option) ? JSON.stringify(option) : String(option);
    switch (schema) {
        case STRING:
            if (isObject(option)) {
                return { coercedValue: stringifiedOption, schema: STRING, isValid: false };
            }

            // JSON.parse rounds large numeric strings and we want to preserve the exact string representation.
            if (isNumber(option) && !Number.isSafeInteger(Number(option))) {
                option = rawOption;
            }

            return { coercedValue: String(option), schema: STRING, isValid: true };
        case INTEGER:
            // check for string 'true' or 'false' to evaluate to 1 or 0 respectively
            if (isString(option) && option.toLocaleLowerCase() === 'true') {
                return { coercedValue: 1, schema: INTEGER, isValid: true };
            }
            if (isString(option) && option.toLocaleLowerCase() === 'false') {
                return { coercedValue: 0, schema: INTEGER, isValid: true };
            }

            if (!isNumeric(option) || !Number.isInteger(Number(option))) {
                return { coercedValue: stringifiedOption, schema: INTEGER, isValid: false };
            }

            return { coercedValue: Number(option), schema: INTEGER, isValid: true };
        case FLOAT:
            // check for string 'true' or 'false' to evaluate to 1.0 or 0.0 respectively
            if (isString(option) && option.toLocaleLowerCase() === 'true') {
                return { coercedValue: 1.0, schema: FLOAT, isValid: true };
            }
            if (isString(option) && option.toLocaleLowerCase() === 'false') {
                return { coercedValue: 0.0, schema: FLOAT, isValid: true };
            }

            if (!isNumeric(option)) {
                return { coercedValue: stringifiedOption, schema: FLOAT, isValid: false };
            }

            return { coercedValue: Number(option), schema: FLOAT, isValid: true };
        case BOOLEAN:
            if (!isBoolean(option) && !isBooleanStringOrNumber(option)) {
                return { coercedValue: stringifiedOption, schema: BOOLEAN, isValid: false };
            }

            // explicitly check for boolean string 'false' and '0' since Boolean('false' or '0') === true.
            // But we want to return false for these cases
            if (isString(option) && (option.toLowerCase() === 'false' || option === '0')) {
                return { coercedValue: false, schema: BOOLEAN, isValid: true };
            }

            return { coercedValue: Boolean(option), schema: BOOLEAN, isValid: true };
        case DATE:
        case TIME:
            if (isObject(option)) {
                return { coercedValue: stringifiedOption, schema, isValid: false };
            }

            return { coercedValue: stringifiedOption, schema, isValid: true };
        case LIST:
            return { coercedValue: stringifiedOption, schema, isValid: false };
        default:
            return { coercedValue: stringifiedOption, schema: 'unknown', isValid: false };
    }
}

export function isBooleanStringOrNumber (option) {
    const booleanStringOptions = ['true', 'false', '1', '0'];
    const booleanNumberOptions = [1, 0];

    if (isNumber(option)) {
        return booleanNumberOptions.includes(option);
    }

    return isString(option) && booleanStringOptions.includes(toLower(option));
}

export function isNumeric (option) {
    return !isNaN(option) && !isNaN(parseFloat(option));
}

export function getEventPropertyLabelBySchemaType (schema) {
    const schemaLabelMap = {
        [STRING]: $t('eventPropertyFilter.Text'),
        [BOOLEAN]: $t('eventPropertyFilter.Boolean'),
        [INTEGER]: $t('eventPropertyFilter.Number (int)'),
        [FLOAT]: $t('eventPropertyFilter.Number (float)'),
        [DATE]: $t('eventPropertyFilter.Date'),
        [TIME]: $t('eventPropertyFilter.Time'),
        [LIST]: $t('eventPropertyFilter.List'),
    };

    return schemaLabelMap[schema];
}
